import styled from "styled-components";
import Box from "../Box";
import { CText } from "@carrefour/design-system-components-react";
import { useStores } from "../../context/storesContext";
import { useTerritories } from "../../context/territoriesContext";
import { useModals } from "../../context/modalsContext";
import { isStoreOpen, formatStoreTime } from "../../utils/storeUtils";
import useBreakPoint from "../../utils/useBreakPoint";

const StatusDot = styled(Box)<{ $isOpen: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${(props) => (props.$isOpen ? "#3fba74" : "#e0251b")};
`;

const StoreNameText = styled(CText)<{ $isMobile: boolean; $isLongName: boolean; $isClickable?: boolean }>`
  color: ${(props) => (props.$isMobile ? "#0e3368" : "#6b7280")};
  font-size: ${(props) => (props.$isMobile ? (props.$isLongName ? "0.60rem" : "0.65rem") : "0.85rem")} !important;
  font-weight: 600;
  cursor: ${(props) => (props.$isClickable ? "pointer" : "default")};
  user-select: none;
  outline: none;

  &:hover {
    ${(props) => props.$isClickable && "color: #0970e6;"}
  }

  &:focus-visible {
    color: #0970e6;
    text-decoration: underline;
  }
`;

const HoursText = styled(CText)<{ size?: string }>`
  color: #6b7280;
  font-size: ${(props) => props.size || "inherit"};
`;

const StoreStatusBadge = () => {
  const { selectedStore, stores, selectStore } = useStores();
  const { territories, selectTerritory } = useTerritories();
  const { showModal, closeModal } = useModals();
  const currentView = useBreakPoint();
  const isMobile = currentView === "phone";

  if (!selectedStore) return null;

  const { openingTime, closingTime, storeName } = selectedStore;
  const open = isStoreOpen(openingTime, closingTime);
  const showHours = openingTime && closingTime;
  const isLongName = !!(storeName && storeName.length >= 19);
  const isMultiStore = stores && stores.length > 1;

  const onStoreSelect = (storeId: string) => {
    const matchingTerritoryKey = territories.find((territory) =>
      territory.stores?.some((territoryStore) => territoryStore.storeKey === storeId)
    )?.key;
    if (matchingTerritoryKey) {
      selectTerritory(matchingTerritoryKey);
    }

    selectStore(storeId);
    closeModal();
  };

  const handleStoreNameClick = () => {
    if (!isMultiStore) return;
    showModal({
      modalToShow: "STORE_SELECTOR_MODAL",
      modalProps: {
        onCancel: closeModal,
        storeArray: stores,
        onSelect: onStoreSelect,
        selectedStore: selectedStore,
        mode: "store",
      },
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isMultiStore) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleStoreNameClick();
    }
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" pr={[2, 4]}>
      <Box display="flex" alignItems="center" style={{ gap: "6px" }}>
        <StoreNameText
          $isMobile={isMobile}
          $isLongName={isLongName}
          $isClickable={isMultiStore}
          onClick={handleStoreNameClick}
          onKeyDown={handleKeyDown}
          tabIndex={isMultiStore ? 0 : undefined}
          role={isMultiStore ? "button" : undefined}
          data-testid="header-store-name"
        >
          {storeName}
        </StoreNameText>
        <StatusDot $isOpen={open} />
      </Box>
      {showHours && (
        <HoursText size=".65rem">
          {formatStoreTime(openingTime!)} - {formatStoreTime(closingTime!)}
        </HoursText>
      )}
    </Box>
  );
};

export default StoreStatusBadge;
